import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Briefcase,
  Plus,
  Eye,
  Trash2,
  RefreshCw,
  TrendingUp,
  Users,
  DollarSign,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Target,
  Archive,
  ArchiveRestore,
  ArrowLeftRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CreateSwapOnBehalfDialog from '../components/admin/CreateSwapOnBehalfDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDealsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [deals, setDeals] = useState([]);
  const [eois, setEois] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('deals');
  const [showInactive, setShowInactive] = useState(false);
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, deal: null });
  const [reviewDialog, setReviewDialog] = useState({ open: false, eoi: null });
  
  // Form state
  const [formData, setFormData] = useState({
    deal_name: '',
    description_html: '',
    borrower_profile: '',
    use_of_proceeds: '',
    repayment_source: '',
    sector: '',
    sub_sector: '',
    category: '',
    product: '',
    grade: '',
    target_amount: '',
    minimum_investment: '50000',
    interest_rate_min: '',
    interest_rate_max: '',
    term_months: '',
    payment_frequency: 'monthly',
    risk_rating: 'B',
    collateral_description: '',
    funding_deadline: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [sectorTree, setSectorTree] = useState({});

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dealsRes, eoisRes, dashRes, sectorsRes, treeRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/deals?include_inactive=${showInactive}`, { headers }),
        axios.get(`${API_URL}/api/admin/eois`, { headers }),
        axios.get(`${API_URL}/api/admin/deals/dashboard`, { headers }),
        axios.get(`${API_URL}/api/deals/sectors`, { headers }),
        axios.get(`${API_URL}/api/deals/sector-tree`, { headers }).catch(() => ({ data: { tree: {} } }))
      ]);
      
      setDeals(dealsRes.data);
      setEois(eoisRes.data);
      setDashboard(dashRes.data);
      setSectors(sectorsRes.data);
      setSectorTree(treeRes.data.tree || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0).replace('ZAR', 'R');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'open':
        return { label: 'Open', color: 'bg-green-100 text-green-700 border-green-200' };
      case 'closing_soon':
        return { label: 'Closing Soon', color: 'bg-amber-100 text-amber-700 border-amber-200' };
      case 'fully_subscribed':
        return { label: 'Fully Subscribed', color: 'bg-blue-100 text-blue-700 border-blue-200' };
      case 'closed':
        return { label: 'Closed', color: 'bg-gray-100 text-gray-600 border-gray-200' };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-600' };
    }
  };

  const getEOIStatusConfig = (status) => {
    switch (status) {
      case 'submitted':
        return { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock };
      case 'accepted':
        return { label: 'Accepted', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 };
      case 'declined':
        return { label: 'Declined', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle };
      case 'withdrawn':
        return { label: 'Withdrawn', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: AlertCircle };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-600', icon: Clock };
    }
  };

  const handleCreateDeal = async () => {
    if (!formData.deal_name || !formData.target_amount || !formData.interest_rate_min) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/deals`, {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        minimum_investment: parseFloat(formData.minimum_investment),
        interest_rate_min: parseFloat(formData.interest_rate_min),
        interest_rate_max: parseFloat(formData.interest_rate_max),
        term_months: parseInt(formData.term_months)
      }, { headers });

      toast.success('Deal created — jumping to Security to attach documents & photos');
      setCreateDialog(false);
      resetForm();
      // Deal-create returns the newly-created row; navigate straight to its
      // Security page so the admin can attach docs/photos/assets right away.
      const newId = r.data?.deal_id || r.data?.deal?.id || r.data?.id;
      if (newId) {
        navigate(`/admin/deals/${newId}/security`);
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create deal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (dealId, newStatus) => {
    try {
      await axios.post(`${API_URL}/api/admin/deals/${dealId}/status?status=${newStatus}`, null, { headers });
      toast.success(`Deal status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!confirm('Are you sure you want to delete this deal?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/deals/${dealId}`, { headers });
      toast.success('Deal deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deal');
    }
  };

  const handleToggleActive = async (deal) => {
    const willBeInactive = deal.is_active !== false;   // current == true -> going inactive
    const confirmText = willBeInactive
      ? `Archive "${deal.deal_name}"?\n\nInactive deals are hidden from active views and investor listings, but retained for audit.`
      : `Restore "${deal.deal_name}" as active?`;
    if (!confirm(confirmText)) return;
    try {
      const r = await axios.post(`${API_URL}/api/admin/deals/${deal.id}/toggle-active`, null, { headers });
      toast.success(r.data.is_active ? 'Deal restored' : 'Deal archived (inactive)');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle deal state');
    }
  };

  const handleReviewEOI = async (action) => {
    if (!reviewDialog.eoi) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/eois/${reviewDialog.eoi.id}/review`, {
        action
      }, { headers });
      
      toast.success(`EOI ${action === 'accept' ? 'accepted' : 'declined'}`);
      setReviewDialog({ open: false, eoi: null });
      fetchData();
    } catch (error) {
      toast.error('Failed to process EOI');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      deal_name: '',
      description_html: '',
      borrower_profile: '',
      use_of_proceeds: '',
      repayment_source: '',
      sector: '',
      sub_sector: '',
      category: '',
      product: '',
      grade: '',
      target_amount: '',
      minimum_investment: '50000',
      interest_rate_min: '',
      interest_rate_max: '',
      term_months: '',
      payment_frequency: 'monthly',
      risk_rating: 'B',
      collateral_description: '',
      funding_deadline: ''
    });
  };

  const pendingEois = eois.filter(e => e.status === 'submitted');

  return (
    <div className="space-y-6" data-testid="admin-deals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Manage Deals</h1>
          <p className="text-[#6C757D] mt-1">Create and manage investment opportunities</p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 text-sm text-[#6C757D] cursor-pointer mr-2" data-testid="show-inactive-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded"
            />
            Show inactive
          </label>
          <Button variant="outline" size="sm" onClick={fetchData} className="border-gray-200">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
            onClick={() => { resetForm(); setCreateDialog(true); }}
            data-testid="create-deal-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Deal
          </Button>
          <CreateSwapOnBehalfDialog
            onCreated={fetchData}
            trigger={
              <Button variant="outline" className="border-gray-200" data-testid="deals-create-swap-btn">
                <ArrowLeftRight className="h-4 w-4 mr-2" />
                Create swap on behalf of
              </Button>
            }
          />
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{dashboard.deals.total}</p>
                  <p className="text-xs text-[#6C757D]">Total Deals</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{dashboard.deals.open}</p>
                  <p className="text-xs text-[#6C757D]">Open</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Target className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{formatCurrency(dashboard.amounts.total_target)}</p>
                  <p className="text-xs text-[#6C757D]">Target</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{formatCurrency(dashboard.amounts.total_committed)}</p>
                  <p className="text-xs text-[#6C757D]">Committed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#212529]">{dashboard.eois.conversion_rate}%</p>
                  <p className="text-xs text-[#6C757D]">Conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="deals" className="data-[state=active]:bg-white">
            Deals ({deals.length})
          </TabsTrigger>
          <TabsTrigger value="eois" className="data-[state=active]:bg-white">
            EOIs ({pendingEois.length} pending)
          </TabsTrigger>
        </TabsList>

        {/* Deals Tab */}
        <TabsContent value="deals">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : deals.length === 0 ? (
                <div className="p-12 text-center">
                  <Briefcase className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-[#6C757D]">No deals created yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Deal</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>EOIs</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deals.map((deal) => {
                      const statusConfig = getStatusConfig(deal.status);
                      const isInactive = deal.is_active === false;
                      return (
                        <TableRow key={deal.id} className={isInactive ? 'opacity-60 bg-slate-50' : ''}>
                          <TableCell>
                            <div className="max-w-[250px]">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-[#212529] truncate">{deal.deal_name}</p>
                                {isInactive && (
                                  <Badge className="bg-gray-200 text-gray-700 border border-gray-300" data-testid={`inactive-badge-${deal.id}`}>
                                    Inactive
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-[#6C757D]">
                                {deal.risk_rating} • {deal.interest_rate_min}-{deal.interest_rate_max}% • {deal.term_months}m
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>{formatCurrency(deal.target_amount)}</TableCell>
                          <TableCell>
                            <div className="w-24">
                              <Progress value={deal.progress_percentage} className="h-2" />
                              <p className="text-xs text-[#6C757D] mt-1">{deal.progress_percentage}%</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <span className="text-amber-600">{deal.eoi_stats?.pending || 0}</span>
                              {' / '}
                              <span className="text-green-600">{deal.eoi_stats?.accepted || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={deal.status}
                              onValueChange={(v) => handleUpdateStatus(deal.id, v)}
                            >
                              <SelectTrigger className="w-36 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="closing_soon">Closing Soon</SelectItem>
                                <SelectItem value="fully_subscribed">Fully Subscribed</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/admin/deals/${deal.id}/security`)}
                                data-testid={`deal-security-btn-${deal.id}`}
                                title="Loan proposal + security schedule + repayment timeline"
                                className="text-[#1D2D2A] border-emerald-200 hover:bg-emerald-50"
                              >
                                Security
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(deal)}
                                className={isInactive ? 'text-emerald-600 hover:text-emerald-700' : 'text-amber-600 hover:text-amber-700'}
                                title={isInactive ? 'Restore deal' : 'Archive (mark inactive)'}
                                data-testid={`toggle-active-btn-${deal.id}`}
                              >
                                {isInactive ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDeal(deal.id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EOIs Tab */}
        <TabsContent value="eois">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : eois.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-[#6C757D]">No expressions of interest yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Investor</TableHead>
                      <TableHead>Deal</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eois.map((eoi) => {
                      const statusConfig = getEOIStatusConfig(eoi.status);
                      const StatusIcon = statusConfig.icon;
                      return (
                        <TableRow key={eoi.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-[#212529]">
                                {eoi.investor?.first_name} {eoi.investor?.last_name}
                              </p>
                              <p className="text-xs text-[#6C757D]">{eoi.investor?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm truncate max-w-[150px]">{eoi.deal?.deal_name}</p>
                          </TableCell>
                          <TableCell className="font-medium">{formatCurrency(eoi.amount)}</TableCell>
                          <TableCell className="text-sm text-[#6C757D]">
                            {formatDate(eoi.submitted_at)}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${statusConfig.color} border`}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {eoi.status === 'submitted' && (
                              <Button
                                size="sm"
                                className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                                onClick={() => setReviewDialog({ open: true, eoi })}
                                data-testid={`review-eoi-${eoi.id}`}
                              >
                                Review
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Deal Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Create New Deal</DialogTitle>
            <DialogDescription>Add a new investment opportunity</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2">
              <label className="text-sm font-medium">Deal Name *</label>
              <Input
                value={formData.deal_name}
                onChange={(e) => setFormData(f => ({ ...f, deal_name: e.target.value }))}
                placeholder="e.g., Western Cape Logistics Fleet"
                className="border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Sector *</label>
              <Select value={formData.sector} onValueChange={(v) => setFormData(f => ({ ...f, sector: v, sub_sector: '', category: '', product: '', grade: '' }))}>
                <SelectTrigger className="border-gray-200" data-testid="deal-sector-select">
                  <SelectValue placeholder="Select sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectors.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Sub-Sector</label>
              <Select
                value={formData.sub_sector}
                onValueChange={(v) => setFormData(f => ({ ...f, sub_sector: v, category: '', product: '', grade: '' }))}
                disabled={!formData.sector}
              >
                <SelectTrigger className="border-gray-200" data-testid="deal-sub-sector-select">
                  <SelectValue placeholder={formData.sector ? "Select sub-sector" : "Select sector first"} />
                </SelectTrigger>
                <SelectContent>
                  {formData.sector && sectorTree[formData.sector.charAt(0).toUpperCase() + formData.sector.slice(1)] &&
                    Object.keys(sectorTree[formData.sector.charAt(0).toUpperCase() + formData.sector.slice(1)]).map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub.replace(/_/g, ' ')}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData(f => ({ ...f, category: v, product: '', grade: '' }))}
                disabled={!formData.sub_sector}
              >
                <SelectTrigger className="border-gray-200" data-testid="deal-category-select">
                  <SelectValue placeholder={formData.sub_sector ? "Select category" : "Select sub-sector first"} />
                </SelectTrigger>
                <SelectContent>
                  {formData.sector && formData.sub_sector &&
                    sectorTree[formData.sector.charAt(0).toUpperCase() + formData.sector.slice(1)]?.[formData.sub_sector] &&
                    Object.keys(sectorTree[formData.sector.charAt(0).toUpperCase() + formData.sector.slice(1)][formData.sub_sector]).map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat.replace(/_/g, ' ')}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Risk Rating *</label>
              <Select value={formData.risk_rating} onValueChange={(v) => setFormData(f => ({ ...f, risk_rating: v }))}>
                <SelectTrigger className="border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A - Low Risk</SelectItem>
                  <SelectItem value="B">B - Medium Risk</SelectItem>
                  <SelectItem value="C">C - Higher Risk</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Target Amount (ZAR) *</label>
              <Input
                type="number"
                value={formData.target_amount}
                onChange={(e) => setFormData(f => ({ ...f, target_amount: e.target.value }))}
                placeholder="e.g., 1000000"
                className="border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Min Investment (ZAR)</label>
              <Input
                type="number"
                value={formData.minimum_investment}
                onChange={(e) => setFormData(f => ({ ...f, minimum_investment: e.target.value }))}
                className="border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Interest Rate Min (%) *</label>
              <Input
                type="number"
                value={formData.interest_rate_min}
                onChange={(e) => setFormData(f => ({ ...f, interest_rate_min: e.target.value }))}
                placeholder="e.g., 24"
                className="border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Interest Rate Max (%) *</label>
              <Input
                type="number"
                value={formData.interest_rate_max}
                onChange={(e) => setFormData(f => ({ ...f, interest_rate_max: e.target.value }))}
                placeholder="e.g., 28"
                className="border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Term (months) *</label>
              <Input
                type="number"
                value={formData.term_months}
                onChange={(e) => setFormData(f => ({ ...f, term_months: e.target.value }))}
                placeholder="e.g., 12"
                className="border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Payment Frequency</label>
              <Select value={formData.payment_frequency} onValueChange={(v) => setFormData(f => ({ ...f, payment_frequency: v }))}>
                <SelectTrigger className="border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="bullet">Bullet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Funding Deadline *</label>
              <Input
                type="date"
                value={formData.funding_deadline.split('T')[0]}
                onChange={(e) => setFormData(f => ({ ...f, funding_deadline: e.target.value }))}
                className="border-gray-200"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <label className="text-sm font-medium">Borrower Profile</label>
              <Textarea
                value={formData.borrower_profile}
                onChange={(e) => setFormData(f => ({ ...f, borrower_profile: e.target.value }))}
                placeholder="Anonymised borrower description..."
                rows={2}
                className="border-gray-200"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <label className="text-sm font-medium">Use of Proceeds</label>
              <Textarea
                value={formData.use_of_proceeds}
                onChange={(e) => setFormData(f => ({ ...f, use_of_proceeds: e.target.value }))}
                placeholder="What the funds will be used for..."
                rows={2}
                className="border-gray-200"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <label className="text-sm font-medium">Collateral/Security</label>
              <Textarea
                value={formData.collateral_description}
                onChange={(e) => setFormData(f => ({ ...f, collateral_description: e.target.value }))}
                placeholder="Security arrangements..."
                rows={2}
                className="border-gray-200"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button
              className="bg-[#1D2D2A] hover:bg-[#243D37] text-white"
              onClick={handleCreateDeal}
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review EOI Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => {
        if (!open) setReviewDialog({ open: false, eoi: null });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Review Expression of Interest</DialogTitle>
            <DialogDescription>Accept or decline this investment request</DialogDescription>
          </DialogHeader>

          {reviewDialog.eoi && (
            <div className="py-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#6C757D]">Investor</p>
                    <p className="font-medium">{reviewDialog.eoi.investor?.first_name} {reviewDialog.eoi.investor?.last_name}</p>
                  </div>
                  <div>
                    <p className="text-[#6C757D]">Amount</p>
                    <p className="font-medium">{formatCurrency(reviewDialog.eoi.amount)}</p>
                  </div>
                  <div>
                    <p className="text-[#6C757D]">Deal</p>
                    <p className="font-medium">{reviewDialog.eoi.deal?.deal_name}</p>
                  </div>
                  <div>
                    <p className="text-[#6C757D]">FICA Status</p>
                    <Badge className={reviewDialog.eoi.profile?.fica_status === 'compliant' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                      {reviewDialog.eoi.profile?.fica_status || 'Unknown'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, eoi: null })}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReviewEOI('decline')}
              disabled={submitting}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => handleReviewEOI('accept')}
              disabled={submitting}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDealsPage;
